import type { CelestialBodyDefinition, BodyRuntimeState } from "../types/celestialBody";
import type { Vector3Data } from "../types/astronomy";
import { addVectors, zeroVector, vectorLength } from "./kepler";
import {
  calculateOrbitalPosition,
  calculateOrbitalSpeedKmS,
} from "./orbitalPosition";
import { calculateBodyRotation } from "./rotation";
import { SUN_MU_KM3_S2 } from "./constants";

function getParentMu(parent: CelestialBodyDefinition | undefined): number {
  return parent?.gravitationalParameterKm3S2 ?? SUN_MU_KM3_S2;
}

/**
 * Resolve posição absoluta recursivamente (luas → planeta → Sol).
 */
export function calculateAbsolutePosition(
  body: CelestialBodyDefinition,
  julianDate: number,
  catalogById: Map<string, CelestialBodyDefinition>,
  visiting: Set<string> = new Set(),
): Vector3Data {
  if (!body.orbit || !body.parentId) {
    return zeroVector();
  }

  if (visiting.has(body.id)) {
    console.warn(`Ciclo detectado na hierarquia orbital: ${body.id}`);
    return zeroVector();
  }

  visiting.add(body.id);
  const relative = calculateOrbitalPosition(body.orbit, julianDate);
  const parent = catalogById.get(body.parentId);

  if (!parent || parent.id === "sun" || !parent.orbit) {
    visiting.delete(body.id);
    return relative;
  }

  const parentAbsolute = calculateAbsolutePosition(
    parent,
    julianDate,
    catalogById,
    visiting,
  );
  visiting.delete(body.id);
  return addVectors(parentAbsolute, relative);
}

export function calculateBodyRuntimeState(
  body: CelestialBodyDefinition,
  julianDate: number,
  catalogById: Map<string, CelestialBodyDefinition>,
): BodyRuntimeState {
  const relative = body.orbit
    ? calculateOrbitalPosition(body.orbit, julianDate)
    : zeroVector();

  const absolute = calculateAbsolutePosition(body, julianDate, catalogById);
  const parent = body.parentId ? catalogById.get(body.parentId) : undefined;
  const distance = vectorLength(relative);
  const speed = body.orbit
    ? calculateOrbitalSpeedKmS(
        distance,
        body.orbit.semiMajorAxisKm,
        getParentMu(parent),
      )
    : 0;

  // Vetor tangencial aproximado no plano eclíptico (direção perpendicular à posição relativa).
  const tangential: Vector3Data =
    distance > 0
      ? {
          x: (-relative.y / distance) * speed,
          y: (relative.x / distance) * speed,
          z: 0,
        }
      : zeroVector();

  return {
    bodyId: body.id,
    absolutePositionKm: absolute,
    relativePositionKm: relative,
    velocityKmS: tangential,
    rotationRad: calculateBodyRotation(body.rotation, julianDate),
    source: "kepler",
  };
}

export function calculateAllBodyStates(
  bodies: CelestialBodyDefinition[],
  julianDate: number,
): BodyRuntimeState[] {
  const catalogById = new Map(bodies.map((b) => [b.id, b]));
  return bodies.map((body) =>
    calculateBodyRuntimeState(body, julianDate, catalogById),
  );
}
